import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AccountMembership, APIService, AuthorizationLevel, CreateCustomPropertyDefinitionsInput, CreateUserInput, InviteUserInput, UpdateAccountInput, UpdateUserInput } from '../../../API.service.custom';
import { DataShareService } from '../../../../services/data-share.service';
import { ToastrService } from 'ngx-toastr';
import localforage from 'localforage';
import { NgClass } from '@angular/common';
import { AuthService, WORKSPACE_ACCESS_PENDING_TOAST } from '../../../auth/auth.service';
import { AiWorkspaceSetupService } from './ai-workspace-setup.service';
import { SALESMAX_SUPPORT_ACCOUNT_IDS } from 'app/core/access/route-access-policy';
import { AiWorkspaceSuggestions, WizardStepState, WIZARD_STEPS } from './ai-workspace-setup.models';
import { WsAccountBasicsComponent } from './steps/ws-account-basics.component';
import { WsCallOutcomesComponent } from './steps/ws-call-outcomes.component';
import { WsDealStagesComponent } from './steps/ws-deal-stages.component';
import { WsProductsComponent } from './steps/ws-products.component';
import { WsVisitOutcomesComponent } from './steps/ws-visit-outcomes.component';
import { WsQualificationFormComponent } from './steps/ws-qualification-form.component';
import { WsInviteTeamComponent } from './steps/ws-invite-team.component';
import { WsImportLeadsComponent } from './steps/ws-import-leads.component';
import { WsFinishComponent } from './steps/ws-finish.component';

const LOADING_MESSAGES = [
  'Understanding your business...',
  'Designing your sales process...',
  'Customizing call outcomes...',
  'Planning deal stages...',
  'Identifying products & services...',
  'Creating custom fields...',
  'Building your qualification form...',
  'Setting up your work pipeline...',
  'Almost done...',
];

@Component({
  selector: 'app-ai-workspace-setup',
  templateUrl: './ai-workspace-setup.component.html',
  styleUrls: ['./ai-workspace-setup.component.scss'],
  imports: [
    NgClass,
    WsAccountBasicsComponent,
    WsCallOutcomesComponent,
    WsDealStagesComponent,
    WsProductsComponent,
    WsVisitOutcomesComponent,
    WsQualificationFormComponent,
    WsInviteTeamComponent,
    WsImportLeadsComponent,
    WsFinishComponent,
  ]
})
export class AiWorkspaceSetupComponent implements OnInit, OnDestroy {
  steps = WIZARD_STEPS;
  currentStep = 0;
  stepStates: WizardStepState[] = WIZARD_STEPS.map(() => ({ completed: false, skipped: false }));
  suggestions: AiWorkspaceSuggestions = null;
  isGenerating = false;
  generationFailed = false;
  isRegenerating = false;
  loadingMessage = LOADING_MESSAGES[0];
  private loadingInterval: any;

  businessDescription = '';
  extractedData: any = null;  // Full context from website extraction (products, industry, location, etc.)
  createdAccountId: string = null;
  activeAccount: AccountMembership = null;
  invitedUserIds: string[] = [];

  // Trial mode (set via ?trial=true query param from salesmax-support)
  // Only allowed from Salesmax accounts (shared set — see route-access-policy).
  settingUpTrialAccount = false;
  prefillWebsite = '';

  private onDestroy$ = new Subject<void>();
  private activeAccountSub: any;

  private currentUserId: string = null;

  constructor(
    private aiService: AiWorkspaceSetupService,
    private api: APIService,
    private dataService: DataShareService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.queryParams['preview'] === 'true') {
      this.toastr.info('AI setup preview has ended. Use Create New Workspace to run the live setup flow.');
      this.router.navigate(['/home/dashboard']);
      return;
    }

    // Trial mode requested via ?trial=true — only allowed from Salesmax accounts
    const trialRequested = this.route.snapshot.queryParams['trial'] === 'true';

    // Pre-fill website from marketing site redirect (via ?website=...)
    this.prefillWebsite = this.route.snapshot.queryParams['website'] || '';

    // Fetch current userId from Cognito (needed for API mutations in child steps)
    this.auth.currentAuthenticatedUser().then(cognitoUser => {
      this.currentUserId = cognitoUser.getSignInUserSession()?.getIdToken()?.payload?.userId;
    }).catch(() => {});

    this.activeAccountSub = this.dataService.getActiveAccount()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((account: AccountMembership) => {
        if (account) {
          this.activeAccount = account;
          this.createdAccountId = account.accountId;

          // Trial mode: verify caller is from a Salesmax account
          if (trialRequested && !this.settingUpTrialAccount) {
            if (SALESMAX_SUPPORT_ACCOUNT_IDS.has(account.accountId)) {
              this.settingUpTrialAccount = true;
            } else {
              this.toastr.error('Trial account creation is only available from the Salesmax workspace');
              this.router.navigate(['/home/dashboard']);
            }
          }
        } else if (trialRequested && !this.settingUpTrialAccount) {
          // No active account and trial not yet started — redirect
          this.router.navigate(['/join-workspace']);
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.stopLoadingAnimation();
  }

  get currentStepKey(): string {
    return this.steps[this.currentStep]?.key || '';
  }

  /** Show sidebar only after Organization step + AI generation are done */
  get showSidebar(): boolean {
    return this.currentStep > 0 && !this.isGenerating;
  }

  // --- Step navigation ---

  goToStep(index: number) {
    if (index <= this.currentStep || this.stepStates[index - 1]?.completed || this.stepStates[index - 1]?.skipped) {
      this.currentStep = index;
    }
  }

  nextStep() {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  skipStep() {
    this.stepStates[this.currentStep] = { completed: false, skipped: true };
    this.nextStep();
  }

  canSkipCurrentStep(): boolean {
    return this.steps[this.currentStep]?.skippable && !this.isGenerating;
  }

  isStepAccessible(index: number): boolean {
    if (index === 0) return true;
    if (index <= this.currentStep) return true;
    for (let i = 0; i < index; i++) {
      if (!this.stepStates[i].completed && !this.stepStates[i].skipped) return false;
    }
    return true;
  }

  // --- Step completion handlers ---

  async onAccountBasicsCompleted(event: { accountId: string; businessDescription: string; extractedData?: any; trialUserId?: string; website?: string; detectedLogoUrl?: string; gstin?: string }) {
    this.createdAccountId = event.accountId;
    this.businessDescription = event.businessDescription;
    this.extractedData = event.extractedData || null;
    this.stepStates[0] = { completed: true, skipped: false };

    // For trial accounts: add Salesmax user as SUPERADMIN + switch to trial account
    // (must happen BEFORE we overwrite activeAccount, since it uses activeAccount.user)
    if (this.settingUpTrialAccount && event.accountId) {
      try {
        if (!(await this.addSalesmaxUserToTrialAccount(event.accountId, event.trialUserId))) {
          // Signed out mid-flow; nothing below has a session to run under.
          return;
        }
        await this.switchToTrialAccount(event.accountId);

        // Now that Salesmax user is a member, apply the deferred updates
        // (ws-account-basics skips these in trial mode since auth wasn't ready)
        const updateInput = <UpdateAccountInput>{};
        updateInput.id = event.accountId;
        updateInput.webPreferences = [
          { key: 'businessDescription', value: event.businessDescription },
          ...(event.website ? [{ key: 'businessWebsite', value: event.website }] : []),
        ];
        if (event.detectedLogoUrl) {
          updateInput.businessLogoUrl = event.detectedLogoUrl;
        }
        if (event.gstin) {
          updateInput.taxId = event.gstin;
          updateInput.taxIdType = 'GST';
        }
        await this.api.UpdateAccount(updateInput).catch(e => console.error('Error saving deferred account updates:', e));
      } catch (e: any) {
        console.error('[AI Workspace Setup] Error in trial account setup:', e);
        this.toastr.error('Error setting up trial account access');
      }
    }

    // Build activeAccount for child components (this route is outside DefaultLayout
    // so dataService.getActiveAccount() may not have the newly created account)
    // In trial mode, use the trial user (account owner) as userId — not the Salesmax operator
    const userId = (this.settingUpTrialAccount && event.trialUserId)
      ? event.trialUserId
      : (this.currentUserId || this.activeAccount?.userId);
    if (event.accountId && userId) {
      this.activeAccount = {
        __typename: 'AccountMembership',
        id: `${event.accountId}:${userId}`,
        accountId: event.accountId,
        userId: userId,
        authorizationLevel: AuthorizationLevel.SUPERADMIN,
        userAccountStatus: 'ACTIVE' as any,
      } as AccountMembership;
    }

    this.generateSuggestions();
  }

  /**
   * Add current Salesmax user as SUPERADMIN in the trial account.
   * Resolves false only when a sign-out intervened and the flow must stop.
   */
  private async addSalesmaxUserToTrialAccount(trialAccountId: string, trialUserId: string): Promise<boolean> {
    if (!this.activeAccount?.user) return true;
    const payload = <InviteUserInput>{};
    payload.invitedUser = <CreateUserInput>{};
    payload.invitedUser.registeredNumber = this.activeAccount.user.registeredNumber;
    payload.invitedUser.registeredEmail = this.activeAccount.user.registeredEmail;
    payload.invitedUser.name = this.activeAccount.user.name;
    payload.invitedUserAuthorizationLevel = AuthorizationLevel.SUPERADMIN;
    payload['inviteeAccountId'] = trialAccountId;
    if (trialUserId) {
      payload['inviteeUserIdIfTrial'] = trialUserId;
    }
    await this.api.InviteUser(payload);
    console.log('[AI Workspace Setup] Salesmax user added as SUPERADMIN to trial account');
    // This invite changes the current user's own membership, so their token
    // must be re-minted before the account-scoped calls that follow.
    const access = await this.auth.awaitAccountInToken(trialAccountId);
    if (access.cancelled) {
      return false;
    }
    if (!access.inToken) {
      this.toastr.warning(WORKSPACE_ACCESS_PENDING_TOAST.message, WORKSPACE_ACCESS_PENDING_TOAST.title);
    }
    return true;
  }

  /** Switch browser context to the trial account */
  private async switchToTrialAccount(trialAccountId: string) {
    localforage.setItem('lastActiveAccountSelectedInBrowser', trialAccountId);
    const input = <UpdateUserInput>{};
    input.lastSelectedAccountWeb = trialAccountId;
    input.id = this.activeAccount.userId;
    await this.api.UpdateUser(input);
    this.dataService.setActiveAccount(null);
    console.log('[AI Workspace Setup] Switched to trial account:', trialAccountId);
  }

  skipAiAndProceed() {
    this.generationFailed = false;
    this.nextStep();
  }

  onStepCompleted(stepIndex: number) {
    this.stepStates[stepIndex] = { completed: true, skipped: false };
    this.nextStep();
  }

  onTeamStepCompleted(userIds: string[]) {
    this.invitedUserIds = userIds || [];
  }

  onTeamStepReady() {
    this.onStepCompleted(6);
  }

  async onFeedbackRequested(section: string, feedback: string) {
    if (this.isRegenerating) return;
    this.isRegenerating = true;
    try {
      const previousData = this.suggestions[section];
      const newData = await this.aiService.regenerateSection(
        section, this.businessDescription, previousData, feedback, this.extractedData
      );
      this.suggestions = { ...this.suggestions, [section]: newData };
      this.toastr.success('AI suggestions updated');
    } catch (e: any) {
      console.error('Error regenerating section:', e);
      this.toastr.error(e.message || 'Failed to regenerate. Please try again.');
    } finally {
      this.isRegenerating = false;
    }
  }

  openCalendly() {
    // Load Calendly CSS if not already present
    if (!document.querySelector('link[href*="calendly.com"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }

    // Load Calendly JS and open popup
    const existingScript = document.querySelector('script[src*="calendly.com"]');
    if (existingScript && (window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({ url: 'https://calendly.com/salesmax/15min' });
    } else {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.onload = () => {
        (window as any).Calendly.initPopupWidget({ url: 'https://calendly.com/salesmax/15min' });
      };
      document.head.appendChild(script);
    }
  }

  onFinish() {
    // Stamp onboarding complete
    if (this.createdAccountId) {
      const input = <UpdateAccountInput>{};
      input.id = this.createdAccountId;
      input.onboardingStatus = JSON.stringify({ aiSetupCompleted: true, completedAt: new Date().toISOString() });
      this.api.UpdateAccount(input).catch(e => console.error('Error updating onboarding status:', e));
    }
    this.router.navigate(['/home/dashboard']);
  }

  // --- AI Generation ---

  async generateSuggestions() {
    this.isGenerating = true;
    this.generationFailed = false;
    this.startLoadingAnimation();

    try {
      this.suggestions = await this.aiService.generateAllSuggestions(this.businessDescription, this.extractedData);
      console.log('[AI Workspace Setup] Suggestions received:', this.suggestions);

      // Stamp inferred industry on account
      if (this.suggestions.industry && this.suggestions.industry !== 'generic' && this.createdAccountId) {
        const input = <UpdateAccountInput>{};
        input.id = this.createdAccountId;
        input.industry = this.suggestions.industry;
        this.api.UpdateAccount(input).catch(e => console.error('Error updating industry:', e));
      }

      // Auto-apply lifecycle names silently (no user review needed)
      this.autoApplyLifecycleNames();

      this.generationFailed = false;
      this.nextStep();
    } catch (e: any) {
      console.error('Error generating suggestions:', e);
      this.generationFailed = true;
      this.toastr.error(e.message || 'Failed to generate suggestions.');
    } finally {
      this.isGenerating = false;
      this.stopLoadingAnimation();
    }
  }

  private startLoadingAnimation() {
    let index = 0;
    this.loadingMessage = LOADING_MESSAGES[0];
    this.loadingInterval = setInterval(() => {
      index = (index + 1) % LOADING_MESSAGES.length;
      this.loadingMessage = LOADING_MESSAGES[index];
    }, 4000);
  }

  private stopLoadingAnimation() {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }
  }

  /** Silently save AI-suggested lifecycle names without showing a wizard step */
  private async autoApplyLifecycleNames() {
    if (!this.suggestions?.lifecycle_names || !this.createdAccountId) return;

    try {
      const possibleValues = Object.entries(this.suggestions.lifecycle_names).map(([key, name]) => ({
        value: key,
        viewValue: name,
      }));

      const input = <CreateCustomPropertyDefinitionsInput>{
        accountId: this.createdAccountId,
        propertyName: 'leadLifecycleName',
        displayName: 'Lead Lifecycle Name',
        description: 'Lead Lifecycle Name',
        scalarType: 'Option',
        isSystemProperty: true,
        parentObjectType: 'Contact',
        possibleValues,
        createdById: this.activeAccount?.userId,
        updaedById: this.activeAccount?.userId,
      };
      await this.api.CreateCustomPropertyDefinitions(input);
      console.log('[AI Workspace Setup] Lifecycle names auto-applied');
    } catch (e) {
      // Non-fatal — lifecycle names can be configured later in settings
      console.error('[AI Workspace Setup] Error auto-applying lifecycle names:', e);
    }
  }
}
